import { FREE_PLAN_NAME } from '@saaga/shared/constants/billing.constants'
import type { BillingPaymentMethod } from '@saaga/shared/interfaces/billing.interface'
import {
  selectIsBalanceLow,
  selectIsBillingLoaded,
  selectSubscriptionInfo,
  selectTenant,
} from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { useMemo } from 'react'

import type {
  ChatUserInteraction,
  UserInteractionType,
} from '@/components/chat/chat-message.interface'
import { useOrganizationEarlyAccess } from '@/hooks/use-organization-early-access.hook'

const isPaymentMethodExpired = (pm: BillingPaymentMethod | null): boolean => {
  if (!pm?.cardExpMonth || !pm?.cardExpYear) return true
  const now = new Date()
  const expiry = new Date(pm.cardExpYear, pm.cardExpMonth)
  return expiry <= now
}

const interactionTitleMap: Record<UserInteractionType, string> = {
  'upgrade-plan': 'Upgrade Your Plan',
  'add-credit-card': 'Add Payment Method',
  'top-up-balance': 'Top Up Balance',
}

const buildInteractionCard = (interactionType: UserInteractionType): ChatUserInteraction => ({
  id: interactionType,
  type: 'user-interaction',
  interactionType,
  title: interactionTitleMap[interactionType],
  timestamp: new Date().toISOString(),
})

/**
 * Determines which single interaction card to show based on billing context.
 * Shown when balance is at or below the low-balance threshold:
 * 1. Not subscribed (free/null plan) AND not in Early Access → "Upgrade Your Plan"
 * 2. Subscribed (or Early Access) + missing/expired payment method → "Add Payment Method"
 * 3. Subscribed (or Early Access) + valid payment method → "Top Up Balance"
 *
 * Early Access tenants live on the Free plan in billing but are granted
 * Pro-level capabilities — including the ability to top up their balance.
 * "Upgrade Your Plan" is also meaningless for them because paid subscriptions
 * are gated while the backend flag is on.
 */
const resolveInteractionType = (
  isFreePlan: boolean,
  isEarlyAccess: boolean,
  isBalanceLow: boolean,
  paymentMethod: BillingPaymentMethod | null
): UserInteractionType | null => {
  if (!isBalanceLow) return null
  if (isFreePlan && !isEarlyAccess) return 'upgrade-plan'
  if (!paymentMethod || isPaymentMethodExpired(paymentMethod)) return 'add-credit-card'
  return 'top-up-balance'
}

export const useUserInteractionCards = (): ChatUserInteraction[] => {
  const isBillingLoaded = useAppSelector(selectIsBillingLoaded)
  const tenant = useAppSelector(selectTenant)
  const subscriptionInfo = useAppSelector(selectSubscriptionInfo)
  const isBalanceLow = useAppSelector(selectIsBalanceLow)
  const { isEarlyAccess } = useOrganizationEarlyAccess()

  const planName = subscriptionInfo?.planName?.toLowerCase()
  const isFreePlan = !planName || planName === FREE_PLAN_NAME
  const paymentMethod = tenant?.defaultPaymentMethod ?? null

  return useMemo(() => {
    if (!isBillingLoaded) return []
    const type = resolveInteractionType(isFreePlan, isEarlyAccess, isBalanceLow, paymentMethod)
    if (!type) return []
    return [buildInteractionCard(type)]
  }, [isBillingLoaded, isFreePlan, isEarlyAccess, isBalanceLow, paymentMethod])
}
